import { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, TextField, Switch, FormControlLabel, Button,
  Stack, Alert, Select, MenuItem, FormControl, InputLabel, CircularProgress,
  IconButton, InputAdornment,
} from '@mui/material';
import { Save as SaveIcon, Visibility, VisibilityOff } from '@mui/icons-material';
import { ConfiguracionService } from '../services/configuracionService';
import type { ConfiguracionPlanilla } from '../types/configuracion';

export default function ConfiguracionPage() {
  const [config, setConfig] = useState<ConfiguracionPlanilla | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => { loadConfig(); }, []);

  const loadConfig = async () => {
    try { setConfig(await ConfiguracionService.get()); }
    catch { /* handled */ }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    if (!config) return;
    setSaving(true);
    try { await ConfiguracionService.save(config); setMessage('Configuración guardada correctamente'); }
    catch { /* handled */ }
    finally { setSaving(false); }
  };

  const set = (field: keyof ConfiguracionPlanilla, value: any) => {
    if (!config) return;
    setConfig({ ...config, [field]: value });
  };

  if (loading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;
  if (!config) return <Box sx={{ p: 3 }}><Alert severity="error">Error cargando configuración</Alert></Box>;

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>Configuración General de Planilla</Typography>
        <Button variant="contained" startIcon={saving ? <CircularProgress size={18} /> : <SaveIcon />}
          onClick={handleSave} disabled={saving}>Guardar</Button>
      </Stack>

      {message && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage(null)}>{message}</Alert>}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>Parámetros Generales</Typography>
        <Stack direction="row" spacing={3} sx={{ mb: 2 }}>
          <TextField label="Días Laborables/Mes" type="number" size="small" sx={{ width: 180 }}
            value={config.diasLaborablesMes} onChange={(e) => set('diasLaborablesMes', Number(e.target.value))} />
          <TextField label="Horas Laborables/Día" type="number" size="small" sx={{ width: 180 }}
            value={config.horasLaborablesDia} onChange={(e) => set('horasLaborablesDia', Number(e.target.value))} />
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Cálculo Gratificación</InputLabel>
            <Select value={config.tipoCalculoGratificacion} label="Cálculo Gratificación"
              onChange={(e) => set('tipoCalculoGratificacion', e.target.value)}>
              <MenuItem value="SEMESTRAL">Semestral</MenuItem>
              <MenuItem value="PROPORCIONAL">Proporcional</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Cálculo Renta 5ta</InputLabel>
            <Select value={config.tipoCalculoRenta5ta} label="Cálculo Renta 5ta"
              onChange={(e) => set('tipoCalculoRenta5ta', e.target.value)}>
              <MenuItem value="FORMA1">SUNAT Estándar</MenuItem>
              <MenuItem value="FORMA2">Recálculo Mensual</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>Opciones de Cálculo</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
          {([
            ['diasFiscales', 'Días Fiscales (30 días/mes)'],
            ['mesesCompletosGratificacion', 'Meses Completos para Gratificación'],
            ['mesesCompletosCTS', 'Meses Completos para CTS'],
            ['asignacionFamiliarVacaciones', 'Asig. Familiar en Vacaciones'],
            ['asignacionFamiliarSubsidiados', 'Asig. Familiar en Subsidiados'],
            ['descuentoTardanzasIntegro', 'Descuento Tardanzas Íntegro'],
            ['henBaseSueldoMinimo', 'HE Nocturnas Base Sueldo Mínimo'],
            ['renta5taQuincenal', 'Renta 5ta Quincenal'],
            ['ingresoManualRenta5ta', 'Ingreso Manual Renta 5ta'],
            ['noProyectarBonificacionRenta5ta', 'No Proyectar Bonificación en Rta 5ta'],
            ['modificarMontosQuincena', 'Modificar Montos Quincena'],
            ['modificarMontosPlanillon', 'Modificar Montos Planillón'],
            ['ingresarAportesGenerales', 'Ingresar Aportes Generales'],
            ['ingresarDescuentosGenerales', 'Ingresar Descuentos Generales'],
          ] as [keyof ConfiguracionPlanilla, string][]).map(([field, label]) => (
            <FormControlLabel key={field} label={label}
              control={<Switch checked={config[field] as boolean} onChange={(e) => set(field, e.target.checked)} />} />
          ))}
        </Box>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>Configuración Quincenal</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Distribución porcentual de remuneraciones entre quincenas. La 1ra quincena funciona como adelanto
          y la 2da quincena calcula el mes completo descontando el adelanto.
        </Typography>
        <Stack direction="row" spacing={3} alignItems="center">
          <TextField label="% Primera Quincena" type="number" size="small" sx={{ width: 180 }}
            value={config.porcentajePrimeraQuincena}
            onChange={(e) => {
              const val = Number(e.target.value);
              set('porcentajePrimeraQuincena', val);
              set('porcentajeSegundaQuincena', 100 - val);
            }}
            inputProps={{ min: 0, max: 100, step: 5 }}
            helperText="Ej: 40%" />
          <TextField label="% Segunda Quincena" type="number" size="small" sx={{ width: 180 }}
            value={config.porcentajeSegundaQuincena}
            InputProps={{ readOnly: true }}
            helperText="Calculado automáticamente" />
          <FormControlLabel
            control={<Switch checked={config.quincenaSinDescuentosLegales}
              onChange={(e) => set('quincenaSinDescuentosLegales', e.target.checked)} />}
            label="1ra Quincena sin descuentos legales (AFP/ONP/Rta 5ta)" />
        </Stack>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>Asientos Contables</Typography>
        <Stack direction="row" spacing={3} alignItems="center">
          <FormControlLabel control={<Switch checked={config.generarAsientosPorCC} onChange={(e) => set('generarAsientosPorCC', e.target.checked)} />}
            label="Generar por Centro de Costo" />
          <FormControlLabel control={<Switch checked={config.generarAsientosPorTareo} onChange={(e) => set('generarAsientosPorTareo', e.target.checked)} />}
            label="Generar por Tareo" />
          <TextField label="Tipo Doc. Planillón" size="small" sx={{ width: 150 }}
            value={config.tipoDocAsientoPlanillon ?? ''} onChange={(e) => set('tipoDocAsientoPlanillon', e.target.value || null)} />
          <TextField label="Tipo Doc. Provisiones" size="small" sx={{ width: 150 }}
            value={config.tipoDocAsientoProvisiones ?? ''} onChange={(e) => set('tipoDocAsientoProvisiones', e.target.value || null)} />
        </Stack>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>Email / SMTP</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Configuración del servidor SMTP para envío de boletas de pago por correo electrónico.
        </Typography>
        <Stack spacing={2}>
          <Stack direction="row" spacing={3}>
            <TextField label="Servidor SMTP" size="small" sx={{ flex: 1 }}
              placeholder="smtp.gmail.com"
              value={config.smtpHost ?? ''} onChange={(e) => set('smtpHost', e.target.value || null)} />
            <TextField label="Puerto" type="number" size="small" sx={{ width: 120 }}
              value={config.smtpPort} onChange={(e) => set('smtpPort', Number(e.target.value))} />
            <FormControlLabel
              control={<Switch checked={config.smtpEnableSsl} onChange={(e) => set('smtpEnableSsl', e.target.checked)} />}
              label="SSL/TLS" />
          </Stack>
          <Stack direction="row" spacing={3}>
            <TextField label="Email Remitente" size="small" sx={{ flex: 1 }}
              placeholder="planillas@empresa.com"
              value={config.smtpFromEmail ?? ''} onChange={(e) => set('smtpFromEmail', e.target.value || null)} />
            <TextField label="Nombre Remitente" size="small" sx={{ flex: 1 }}
              placeholder="Sistema de Planillas"
              value={config.smtpFromName ?? ''} onChange={(e) => set('smtpFromName', e.target.value || null)} />
          </Stack>
          <TextField label="Contraseña / App Password" size="small" sx={{ maxWidth: 400 }}
            type={showPassword ? 'text' : 'password'}
            value={config.smtpPassword ?? ''} onChange={(e) => set('smtpPassword', e.target.value || null)}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setShowPassword(!showPassword)}>
                      {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
            helperText="Para Gmail, use una Contraseña de Aplicación (no la contraseña normal)" />
        </Stack>
      </Paper>
    </Box>
  );
}
